import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin, Check, User, Loader2, CreditCard, Tag, X } from 'lucide-react';
import api from '../services/api';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { useRazorpay } from 'react-razorpay';
import OTPLoginModal from './OTPLoginModal';
import { toast } from '../hooks/use-toast';

const SPORT_CONFIG = {
  football: { color: 'bg-green-500', label: 'Football' },
  padel: { color: 'bg-blue-500', label: 'Padel' },
  pickleball: { color: 'bg-orange-500', label: 'Pickleball' }
};

const BookingFlow = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useCustomerAuth();
  const { Razorpay } = useRazorpay();
  const [showLogin, setShowLogin] = useState(false);

  // State
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Date, 2: Sport/Court, 3: Time, 4: Confirm
  
  // Booking data
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [customerDetails, setCustomerDetails] = useState({ name: '', email: '' });
  
  // Availability
  const [timeSlots, setTimeSlots] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [payChoice, setPayChoice] = useState('full'); // 'full' | 'partial'

  // Restore customer details from user after login
  useEffect(() => {
    if (isAuthenticated && user) {
      setCustomerDetails(prev => ({
        name: prev.name || user.name || '',
        email: prev.email || user.email || ''
      }));
    }
  }, [isAuthenticated, user]);

  // Load facility
  useEffect(() => {
    loadFacility();
  }, [venueId]);

  // Load availability when date/court changes
  useEffect(() => {
    if (selectedDate && selectedCourt && facility) {
      loadAvailability();
    }
  }, [selectedDate, selectedCourt, facility]);

  const loadFacility = async () => {
    try {
      const facilities = await api.getPublicFacilities();
      const found = facilities.find(f => f.id === venueId);
      setFacility(found);
      if (found?.sports?.length === 1) {
        setSelectedSport(found.sports[0]);
      }
    } catch (error) {
      console.error('Error loading facility:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailability = async () => {
    if (!selectedCourt) return;
    setLoadingSlots(true);
    try {
      const data = await api.getPublicSlotAvailability(selectedDate, facility.id);
      // availability.slots is an object keyed by time, e.g., {"00:00": {...}, "00:30": {...}}
      setAvailability(data.slots || {});
      generateTimeSlots();
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const generateTimeSlots = useCallback(() => {
    if (!facility) return;

    const slots = [];
    let startHour = 6, startMinute = 0, endHour = 23, endMinute = 0;

    if (facility.operating_hours) {
      [startHour, startMinute] = (facility.operating_hours.start || '06:00').split(':').map(Number);
      [endHour, endMinute] = (facility.operating_hours.end || '23:00').split(':').map(Number);
    }

    const startTotal = startHour * 60 + startMinute;
    const endTotal = endHour * 60 + endMinute;

    const emit = (from, to) => {
      for (let t = from; t + 30 <= to; t += 30) {
        const h = Math.floor(t / 60) % 24;
        const m = t % 60;
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    };

    if (endTotal <= startTotal) {
      // Overnight: early morning (00:00 -> close) then evening (open -> midnight)
      emit(0, endTotal);
      emit(startTotal, 24 * 60);
    } else {
      emit(startTotal, endTotal);
    }

    setTimeSlots(slots);
  }, [facility]);

  useEffect(() => {
    if (facility) generateTimeSlots();
  }, [facility, generateTimeSlots]);

  const isSlotAvailable = (time) => {
    // Check if slot has passed for today
    const today = new Date().toISOString().split('T')[0];
    if (selectedDate === today) {
      const now = new Date();
      const [hours, mins] = time.split(':').map(Number);
      const slotTime = new Date();
      slotTime.setHours(hours, mins, 0, 0);
      if (slotTime <= now) return false;
    }
    
    // availability is an object keyed by time, e.g., {"00:00": {sections: {...}}, ...}
    const slotData = availability[time];
    if (!slotData) return true; // If no data, assume available
    
    if (selectedCourt) {
      // Check specific court availability
      const courtData = slotData.sections?.[selectedCourt];
      return courtData?.available !== false;
    }
    
    // Default to full_available if no specific court selected
    return slotData.full_available !== false;
  };

  // Handle moving to confirmation step - check login first
  const handleProceedToPayment = () => {
    if (selectedSlots.length === 0) {
      toast({
        title: "Select Time",
        description: "Please select at least one time slot",
        variant: "destructive"
      });
      return;
    }

    if (!isAuthenticated) {
      // Show OTP login modal instead of redirecting
      setShowLogin(true);
      return;
    }

    setStep(4);
  };

  const toggleSlot = (time) => {
    if (!isSlotAvailable(time)) return;
    
    setSelectedSlots(prev => {
      if (prev.includes(time)) {
        return prev.filter(t => t !== time);
      }
      // Allow multiple consecutive slots
      return [...prev, time].sort();
    });
  };

  const formatTime = (time) => {
    const [hours, mins] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const calculatePrice = () => {
    if (!facility || !selectedCourt || selectedSlots.length === 0) return 0;
    
    // Get court type from court id
    const court = facility.courts?.find(c => c.id === selectedCourt);
    const courtType = court?.court_type || 'quarter';
    
    // Get pricing for this court type
    const pricing = facility.pricing?.[courtType];
    if (!pricing) {
      // Fallback to default pricing
      return selectedSlots.length * 500;
    }
    
    // Determine if weekend
    const bookingDate = new Date(selectedDate);
    const dayOfWeek = bookingDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const dayPricing = isWeekend ? pricing.weekend : pricing.weekday;
    
    // Get peak hours
    const peakHours = facility.peak_hours?.[isWeekend ? 'weekend' : 'weekday'] || [];
    
    // Calculate total price based on slots
    let total = 0;
    selectedSlots.forEach(slot => {
      const [hours] = slot.split(':').map(Number);
      
      // Check if slot is in peak hours
      let isPeak = false;
      for (const peak of peakHours) {
        const [peakStart] = peak.start.split(':').map(Number);
        const [peakEnd] = peak.end.split(':').map(Number);
        if (hours >= peakStart && hours < peakEnd) {
          isPeak = true;
          break;
        }
      }
      
      // Price per 30-min slot (hourly rate / 2)
      const hourlyRate = isPeak ? dayPricing.peak : dayPricing.off_peak;
      total += hourlyRate / 2;
    });
    
    return Math.round(total);
  };

  // Calculate final price after discount
  const getFinalPrice = () => {
    const basePrice = calculatePrice();
    if (appliedCoupon && appliedCoupon.discount_percent) {
      const discountAmount = Math.round(basePrice * appliedCoupon.discount_percent / 100);
      return basePrice - discountAmount;
    }
    return basePrice;
  };

  // Validate coupon code
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast({
        title: "Enter Code",
        description: "Please enter a coupon code",
        variant: "destructive"
      });
      return;
    }

    setValidatingCoupon(true);
    try {
      const basePrice = calculatePrice();
      const result = await api.validateCoupon(couponCode.trim(), facility.id, basePrice);
      
      if (result.valid) {
        setAppliedCoupon({
          code: couponCode.toUpperCase(),
          coupon_id: result.coupon_id,
          discount_percent: result.discount_percent,
          discount_amount: result.discount_amount,
          final_amount: result.final_amount
        });
        toast({
          title: "Coupon Applied!",
          description: result.message,
        });
      } else {
        toast({
          title: "Invalid Coupon",
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to validate coupon",
        variant: "destructive"
      });
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Remove applied coupon
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast({
      title: "Coupon Removed",
      description: "Discount has been removed",
    });
  };

  const getDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    if (dateStr === today.toISOString().split('T')[0]) return 'Today';
    if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Tomorrow';
    
    return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getFilteredCourts = () => {
    if (!facility?.courts) return [];
    if (!selectedSport) return facility.courts;
    return facility.courts.filter(c => c.sport === selectedSport);
  };

  const handleBooking = async () => {
    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    if (selectedSlots.length === 0) {
      toast({
        title: "Select Time",
        description: "Please select at least one time slot",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      // Calculate end time
      const sortedSlots = [...selectedSlots].sort();
      const lastSlot = sortedSlots[sortedSlots.length - 1];
      const [h, m] = lastSlot.split(':').map(Number);
      let endH = h;
      let endM = m + 30;
      if (endM >= 60) { endM -= 60; endH += 1; }
      if (endH >= 24) endH = 0;
      const endTime = `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`;

      const basePrice = calculatePrice();
      const finalAmount = appliedCoupon ? getFinalPrice() : basePrice;

      // slot_type must be the court's type (e.g. 'full','padel','pickleball'),
      // not the sport name — the backend validates it against a fixed list.
      const courtObj = (facility.courts || []).find(c => c.id === selectedCourt);
      const slotType = courtObj?.court_type || courtObj?.sport || selectedSport;

      const bookingData = {
        facility_id: facility.id,
        date: selectedDate,
        start_time: sortedSlots[0],
        end_time: endTime,
        slot_type: slotType,
        court_id: selectedCourt,
        sport: selectedSport,
        customer_name: customerDetails.name || user?.name || 'Guest',
        customer_email: customerDetails.email || user?.email || '',
        customer_phone: user?.phone || '',
        status: 'pending',
        amount: finalAmount,
        amount_paid: 0,
        payment_status: 'pending',
        source: 'online',
        booking_type: 'b2c',
        // Coupon fields
        discount_percent: appliedCoupon?.discount_percent || null,
        discount_amount: appliedCoupon?.discount_amount || null,
        coupon_code: appliedCoupon?.code || null,
        coupon_id: appliedCoupon?.coupon_id || null
      };

      const result = await api.createCustomerBooking(bookingData);
      const bookingId = result.id || result.booking_id;

      // Free booking (zero amount) — already reserved, nothing to collect.
      if (!finalAmount || finalAmount <= 0) {
        toast({ title: "Booking Confirmed!", description: "Your slot has been booked successfully" });
        navigate('/my-bookings');
        return;
      }

      // Partial payment: if the venue allows it and the customer chose "pay part now",
      // collect the configured % now; the full booking amount is unchanged, balance is
      // due at the venue.
      const pp = facility?.partial_payment;
      const partialEnabled = !!(pp?.enabled && pp?.customer_percentage > 0 && pp?.customer_percentage < 100);
      const payNowAmount = (partialEnabled && payChoice === 'partial')
        ? Math.round(finalAmount * pp.customer_percentage / 100)
        : finalAmount;

      // Collect payment online via the venue's own Razorpay account.
      if (!Razorpay) {
        toast({ title: "Payment gateway loading", description: "Please try again in a moment.", variant: "destructive" });
        return;
      }

      const orderData = await api.createPublicPaymentOrder(bookingId, payNowAmount);
      const options = {
        key: orderData.key_id, // venue-specific key (NOT a global key)
        amount: orderData.amount,
        currency: orderData.currency,
        name: facility?.name || "PlaySpaces",
        description: `${SPORT_CONFIG[selectedSport]?.label || selectedSport || 'Court'} booking · ${selectedDate}`,
        order_id: orderData.order_id,
        prefill: {
          name: customerDetails.name || user?.name || '',
          email: customerDetails.email || user?.email || '',
          contact: user?.phone || ''
        },
        theme: { color: "#0A2C93" },
        handler: async (response) => {
          try {
            await api.verifyPublicPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: bookingId
            });
            const balance = finalAmount - payNowAmount;
            toast({
              title: "Payment Successful!",
              description: balance > 0
                ? `Booking confirmed. ₹${balance} due at the venue.`
                : "Your booking is confirmed."
            });
            navigate('/my-bookings');
          } catch (err) {
            toast({ title: "Payment Verification Failed", description: err.message || 'Please contact the venue.', variant: "destructive" });
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast({ title: "Payment Cancelled", description: "Your slot is held as pending — pay from My Bookings or at the venue." });
            navigate('/my-bookings');
          }
        }
      };
      const rzp = new Razorpay(options);
      rzp.open();
    } catch (error) {
      toast({
        title: "Booking Failed",
        description: error.message || "Something went wrong",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2C93]" />
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <p className="font-body text-[#737373] mb-4">Venue not found</p>
        <Link to="/" className="font-heading font-semibold text-[#0A2C93]">Back to venues</Link>
      </div>
    );
  }

  // Redirect to login if not logged in when trying to book
  if (step === 4 && !isAuthenticated) {
    setShowLogin(true);
    setStep(3);
  }

  // Handle login success - proceed to step 4
  const handleLoginSuccess = (userData) => {
    setShowLogin(false);
    setCustomerDetails(prev => ({
      ...prev,
      name: prev.name || userData?.name || '',
      email: prev.email || userData?.email || ''
    }));
    setStep(4);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* OTP Login Modal */}
      {showLogin && (
        <OTPLoginModal
          onClose={() => setShowLogin(false)}
          onSuccess={handleLoginSuccess}
        />
      )}
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button 
              onClick={() => step > 1 ? setStep(step - 1) : navigate(`/venue/${venueId}`)}
              className="flex items-center gap-1 text-[#0A2C93]"
              data-testid="booking-back-btn"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-heading font-semibold text-sm">Back</span>
            </button>
            <h1 className="font-heading font-bold text-[#0A2C93]">Book Slot</h1>
            <div className="w-16"></div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="max-w-3xl mx-auto px-4 pb-3">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className="flex-1">
                <div className={`h-1 rounded-full transition-colors ${s <= step ? 'bg-[#0A2C93]' : 'bg-slate-200'}`}></div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1 text-[10px] font-heading font-semibold text-[#737373]">
            <span>Date</span>
            <span>Court</span>
            <span>Time</span>
            <span>Confirm</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-6">
        {/* Step 1: Select Date */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Select Date</h2>
              <p className="text-slate-500">Choose when you want to play</p>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
              {getDates().map(date => (
                <button
                  key={date}
                  onClick={() => setSelectedDate(date)}
                  className={`flex-shrink-0 px-4 py-3 rounded-xl border-2 transition text-center min-w-[80px] ${
                    selectedDate === date
                      ? 'border-[#0A2C93] bg-[#0A2C93]/5 text-[#0A2C93]'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="text-xs text-slate-500">
                    {new Date(date).toLocaleDateString('en-IN', { weekday: 'short' })}
                  </div>
                  <div className="text-lg font-semibold">
                    {new Date(date).getDate()}
                  </div>
                  <div className="text-xs">
                    {new Date(date).toLocaleDateString('en-IN', { month: 'short' })}
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={!selectedDate}
              className="w-full py-3 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 text-white font-semibold transition flex items-center justify-center gap-2"
            >
              Continue
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 2: Select Sport & Court */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Select Court</h2>
              <p className="text-slate-500">Choose your sport and court</p>
            </div>

            {/* Sport Selection */}
            {facility.sports?.length > 1 && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Sport</label>
                <div className="flex gap-2 flex-wrap">
                  {facility.sports.map(sport => (
                    <button
                      key={sport}
                      onClick={() => {
                        setSelectedSport(sport);
                        setSelectedCourt(null);
                      }}
                      className={`px-4 py-2 rounded-lg border-2 transition ${
                        selectedSport === sport
                          ? 'border-[#0A2C93] bg-[#0A2C93]/5 text-[#0A2C93]'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      {SPORT_CONFIG[sport]?.label || sport}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Court Selection */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Court</label>
              <div className="grid grid-cols-2 gap-3">
                {getFilteredCourts().map(court => (
                  <button
                    key={court.id}
                    onClick={() => setSelectedCourt(court.id)}
                    className={`p-4 rounded-xl border-2 text-left transition ${
                      selectedCourt === court.id
                        ? 'border-[#0A2C93] bg-[#0A2C93]/5'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold text-slate-900">{court.name || court.id}</div>
                    <div className="text-sm text-slate-500 capitalize">{court.sport}</div>
                    {selectedCourt === court.id && (
                      <div className="mt-2 flex items-center gap-1 text-[#0A2C93] text-sm">
                        <Check className="w-4 h-4" />
                        <span>Selected</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setStep(3)}
              disabled={!selectedCourt}
              className="w-full py-3 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 text-white font-semibold transition flex items-center justify-center gap-2"
            >
              Continue
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 3: Select Time Slots */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Select Time</h2>
              <p className="text-slate-500">{formatDateDisplay(selectedDate)} • Tap to select slots</p>
            </div>

            {loadingSlots ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0A2C93]" />
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {timeSlots.map(time => {
                  const available = isSlotAvailable(time);
                  const selected = selectedSlots.includes(time);
                  
                  return (
                    <button
                      key={time}
                      onClick={() => toggleSlot(time)}
                      disabled={!available}
                      className={`py-3 px-2 rounded-lg text-sm font-medium transition ${
                        selected
                          ? 'bg-[#0A2C93]/50 text-white'
                          : available
                            ? 'bg-white border border-slate-200 text-slate-700 hover:border-[#0A2C93]/30'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {formatTime(time)}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-white border border-slate-200"></div>
                <span className="text-slate-600">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#0A2C93]/50"></div>
                <span className="text-slate-600">Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-slate-100"></div>
                <span className="text-slate-600">Booked</span>
              </div>
            </div>

            {/* Selected Summary */}
            {selectedSlots.length > 0 && (
              <div className="bg-[#0A2C93]/5 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-[#0A2C93]">Selected: {selectedSlots.length} slots ({selectedSlots.length * 30} mins)</div>
                    <div className="text-lg font-bold text-[#0A2C93]">₹{calculatePrice()}</div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleProceedToPayment}
              disabled={selectedSlots.length === 0}
              className="w-full py-3 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 text-white font-semibold transition flex items-center justify-center gap-2"
            >
              Continue to Payment
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 4: Confirm & Pay */}
        {step === 4 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Confirm Booking</h2>
              <p className="text-slate-500">Review and complete your booking</p>
            </div>

            {/* Booking Summary Card */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="font-semibold text-slate-900">{facility.name}</h3>
                <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                  <MapPin className="w-4 h-4" />
                  <span>{facility.location || 'Bangalore'}</span>
                </div>
              </div>
              
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span>Date</span>
                  </div>
                  <span className="font-medium text-slate-900">{formatDateDisplay(selectedDate)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="w-4 h-4" />
                    <span>Time</span>
                  </div>
                  <span className="font-medium text-slate-900">
                    {selectedSlots.length > 0 && `${formatTime(selectedSlots[0])} - ${formatTime(selectedSlots[selectedSlots.length - 1])}`}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Court</span>
                  <span className="font-medium text-slate-900">{selectedCourt}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Duration</span>
                  <span className="font-medium text-slate-900">{selectedSlots.length * 30} minutes</span>
                </div>
              </div>
              
              <div className="p-4 bg-slate-50 border-t border-slate-100">
                {/* Coupon Input */}
                {!appliedCoupon ? (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="Enter coupon code"
                          className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93] uppercase font-mono text-sm"
                        />
                      </div>
                      <button
                        onClick={handleApplyCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                        className="px-4 py-2 bg-[#0A2C93] text-white rounded-lg font-medium text-sm hover:bg-[#082273] disabled:bg-slate-300 transition"
                      >
                        {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-[#00D650]/10 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#0A2C93]" />
                      <span className="font-mono font-medium text-[#0A2C93]">{appliedCoupon.code}</span>
                      <span className="text-sm text-[#0A2C93]">({appliedCoupon.discount_percent}% OFF)</span>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-slate-500 hover:text-slate-700 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                {/* Price Display */}
                {appliedCoupon ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Subtotal</span>
                      <span className="line-through">₹{calculatePrice()}</span>
                    </div>
                    <div className="flex items-center justify-between text-[#0A2C93]">
                      <span>Discount ({appliedCoupon.discount_percent}%)</span>
                      <span>-₹{appliedCoupon.discount_amount}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                      <span className="text-lg font-semibold text-slate-900">Total</span>
                      <span className="text-2xl font-bold text-[#0A2C93]">₹{getFinalPrice()}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-slate-900">Total</span>
                    <span className="text-2xl font-bold text-[#0A2C93]">₹{calculatePrice()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Your Details</h3>
              
              <div className="flex items-center gap-3 p-3 bg-[#0A2C93]/5 rounded-xl">
                <User className="w-5 h-5 text-[#0A2C93]" />
                <span className="font-medium text-[#0A2C93]">
                  {user?.name || `+91 ${user?.phone}`}
                </span>
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Name</label>
                <input
                  type="text"
                  value={customerDetails.name}
                  onChange={(e) => setCustomerDetails(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93]"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Email (optional)</label>
                <input
                  type="email"
                  value={customerDetails.email}
                  onChange={(e) => setCustomerDetails(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93]"
                />
              </div>
            </div>

            {/* Partial payment option (only when the venue enables it) */}
            {facility?.partial_payment?.enabled && facility.partial_payment.customer_percentage > 0
              && facility.partial_payment.customer_percentage < 100 && (() => {
              const full = appliedCoupon ? getFinalPrice() : calculatePrice();
              const pct = facility.partial_payment.customer_percentage;
              const partNow = Math.round(full * pct / 100);
              return (
                <div className="space-y-3" data-testid="partial-payment-options">
                  <h3 className="font-semibold text-slate-900">Payment option</h3>
                  <button
                    type="button"
                    onClick={() => setPayChoice('full')}
                    className={`w-full text-left p-3 rounded-xl border transition ${payChoice === 'full' ? 'border-[#0A2C93] bg-[#0A2C93]/5' : 'border-slate-200'}`}
                    data-testid="pay-full-option"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900">Pay full now</span>
                      <span className="font-bold text-[#0A2C93]">₹{full}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPayChoice('partial')}
                    className={`w-full text-left p-3 rounded-xl border transition ${payChoice === 'partial' ? 'border-[#0A2C93] bg-[#0A2C93]/5' : 'border-slate-200'}`}
                    data-testid="pay-partial-option"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900">Pay {pct}% to confirm</span>
                      <span className="font-bold text-[#0A2C93]">₹{partNow}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Pay ₹{partNow} now · ₹{full - partNow} due at the venue</p>
                  </button>
                </div>
              );
            })()}

            {(() => {
              const full = appliedCoupon ? getFinalPrice() : calculatePrice();
              const pp = facility?.partial_payment;
              const isPartial = pp?.enabled && pp?.customer_percentage > 0 && pp?.customer_percentage < 100 && payChoice === 'partial';
              const payNow = isPartial ? Math.round(full * pp.customer_percentage / 100) : full;
              return (
                <button
                  onClick={handleBooking}
                  disabled={submitting}
                  className="w-full py-4 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 text-white font-semibold transition flex items-center justify-center gap-2"
                  data-testid="booking-pay-confirm-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      <span>{isPartial ? `Pay ₹${payNow} to Confirm` : `Pay & Confirm • ₹${payNow}`}</span>
                    </>
                  )}
                </button>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
};

export default BookingFlow;
